import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import homehero1 from '../../assets/homehero1.png';
import homehero2 from '../../assets/homehero2.png';
import homehero3 from '../../assets/homehero3.png';

/** Headlines and routes match the first three article cards; subtitles use each piece’s opening body paragraph. */
const SLIDES = [
  {
    id: 'oncology-surrogate-endpoints',
    image: homehero1,
    title: 'Interpreting surrogate endpoints in oncology trials',
    subtitle:
      'Surrogate endpoints—such as progression-free survival or tumour shrinkage—can speed trials and give an early read on whether a drug is doing something measurable. They are not the same as proof that patients live longer or feel better.',
    readMoreTo: '/articles/oncology-surrogate-endpoints',
  },
  {
    id: 'antibiotic-stewardship-outpatient',
    image: homehero2,
    title: 'Antibiotic stewardship in outpatient settings',
    subtitle:
      'Outpatient prescribing drives a large share of antibiotic use. Stewardship programmes designed for hospitals do not always translate to clinics, urgent care, and telehealth visits.',
    readMoreTo: '/articles/antibiotic-stewardship-outpatient',
  },
  {
    id: 'biosimilars-patient-guide',
    image: homehero3,
    title: 'What patients should know about biosimilars',
    subtitle:
      'A biosimilar is a biological medicine highly similar to an already approved reference product, with no clinically meaningful differences in safety or effectiveness expected from rigorous comparability data.',
    readMoreTo: '/articles/biosimilars-patient-guide',
  },
];

const AUTO_MS = 7000;

export default function HeroSection() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const go = useCallback((delta) => {
    setIndex((i) => (i + delta + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduceMotion(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (reduceMotion || paused) return undefined;
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, AUTO_MS);
    return () => window.clearInterval(id);
  }, [reduceMotion, paused]);

  const slide = SLIDES[index];
  const fade = reduceMotion ? 'duration-0' : 'duration-[900ms]';

  return (
    <section
      className="relative isolate min-h-[92vh] w-full overflow-hidden bg-slate-950"
      aria-roledescription="carousel"
      aria-label="Home highlights"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 bg-cover bg-center transition-opacity ease-out ${fade} ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          style={{ backgroundImage: `url(${s.image})` }}
          aria-hidden={i !== index}
        />
      ))}

      <div
        className="pointer-events-none absolute inset-0 bg-slate-950/55"
        aria-hidden
      />

      <button
        type="button"
        onClick={() => go(-1)}
        className="absolute left-2 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/15 bg-white/10 p-2.5 text-white shadow-sm backdrop-blur transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70 sm:left-4"
        aria-label="Previous slide"
      >
        <ChevronLeft className="h-6 w-6" strokeWidth={2} aria-hidden />
      </button>
      <button
        type="button"
        onClick={() => go(1)}
        className="absolute right-2 top-1/2 z-20 -translate-y-1/2 rounded-full border border-white/15 bg-white/10 p-2.5 text-white shadow-sm backdrop-blur transition hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/70 sm:right-4"
        aria-label="Next slide"
      >
        <ChevronRight className="h-6 w-6" strokeWidth={2} aria-hidden />
      </button>

      <div className="relative z-10 flex min-h-[92vh] flex-col">
        <div className="flex flex-1 flex-col items-center justify-center px-16 py-20 sm:py-24 lg:px-12 lg:py-28">
          <div className="mx-auto flex w-full max-w-5xl flex-col items-center gap-8 text-center sm:gap-10 lg:gap-12 xl:max-w-6xl">
            <h1 className="mx-auto max-w-4xl text-pretty text-xl font-semibold leading-snug tracking-tight text-white sm:text-2xl md:text-3xl lg:text-4xl">
              {slide.title}
            </h1>
            <p className="mx-auto max-w-4xl text-pretty text-base leading-loose text-slate-200/95 sm:text-lg md:max-w-5xl lg:text-xl">
              {slide.subtitle}
            </p>

            <div className="flex justify-center">
              <Link
                to={slide.readMoreTo}
                className="inline-flex w-full max-w-xs items-center justify-center rounded-md bg-brand-500 px-6 py-3.5 text-sm font-semibold text-white shadow-md shadow-brand-900/20 transition hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-200 sm:w-auto sm:min-w-[10rem]"
              >
                Read more
              </Link>
            </div>
          </div>
        </div>

        <div
          className="relative z-20 flex shrink-0 items-center justify-center gap-2 px-16 pb-10 pt-2 lg:px-12 sm:pb-12"
          role="tablist"
          aria-label="Slides"
        >
          {SLIDES.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-10 bg-brand-300' : 'w-1.5 bg-white/35 hover:bg-white/55'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
